import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Alert
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

interface DeleteConfirmationDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    couponCode: string;
}

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
    open,
    onClose,
    onConfirm,
    couponCode
}) => {
    const [confirmText, setConfirmText] = useState('');
    const [error, setError] = useState(false);
    
    // Reset state when dialog opens
    useEffect(() => {
        if (open) {
            setConfirmText('');
            setError(false);
        }
    }, [open]);

    const handleConfirm = () => {
        if (confirmText === 'Delete') {
            onConfirm();
            onClose();
        } else {
            setError(true);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    p: 2
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                color: 'error.main'
            }}>
                <WarningIcon color="error" />
                Confirm Deletion
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" gutterBottom>
                        You are about to delete the coupon with code: <strong>{couponCode}</strong>
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        This action cannot be undone. All associated data including scans, redemptions, and analytics will be permanently deleted.
                    </Alert>
                </Box>
                
                <Typography variant="body2" gutterBottom>
                    To confirm deletion, type <strong>Delete</strong> in the field below:
                </Typography>
                
                <TextField
                    fullWidth
                    value={confirmText}
                    onChange={(e) => {
                        setConfirmText(e.target.value);
                        setError(false);
                    }}
                    error={error}
                    helperText={error ? 'Please type "Delete" to confirm' : ''}
                    placeholder="Type 'Delete' to confirm"
                    size="small"
                    sx={{ mt: 1 }}
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    color="error"
                    variant="contained"
                    disabled={confirmText !== 'Delete'}
                >
                    Delete Permanently
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteConfirmationDialog;
